import { useCallback } from 'react';

import type { InventoryFormSchema } from '@/_pages/settings/inventory-kits/shared/schemas/inventory';
import type {
  VendorDetailsCreateSchema,
  VendorDetailsFormSchema,
  VendorDetailsQuerySchema,
  VendorDetailsUpdateSchema,
} from '@/_pages/settings/inventory-kits/shared/schemas/vendor-details';
import { currencyInput } from '@/_schemas/generic';
import { snackbar } from '@/_shared';

import { useCreateVendorDetailsMutation, useDeleteVendorDetailsMutation, useUpdateVendorDetailsMutation } from './apis';
import { vendorDetailsFormToUpdateSingle } from './converters';

type Vendors = InventoryFormSchema['vendors'];

type CreateUpdateFn = (
  vendorDetails: VendorDetailsFormSchema[],
  vendors: Vendors
) => Promise<{ result: boolean | VendorDetailsFormSchema[]; isRefreshRequired: boolean }>;

type HandleVendors = (
  vendorDetails: VendorDetailsFormSchema[],
  vendors: Vendors,
  dirtyIndices: number[]
) => Promise<{ result: boolean | VendorDetailsFormSchema[]; isRefreshRequired: boolean }>;

export function useVendorDetails(inventory_id: string) {
  const { isPending, mutateAsync } = useCreateVendorDetailsMutation();
  const { isPending: updateIsPending, mutateAsync: updateMutateSync } = useUpdateVendorDetailsMutation();
  const { isPending: deleteIsPending, mutateAsync: deleteMutateSync } = useDeleteVendorDetailsMutation();

  const handleCreate = useCallback(
    async (newV: VendorDetailsCreateSchema) => {
      try {
        const response = await mutateAsync(newV);
        if (response.data) {
          return { ...newV, ...response.data } as VendorDetailsQuerySchema;
        }
      } catch (error) {
        console.error(error);
        return false;
      }
      return false;
    },
    [mutateAsync]
  );

  const handleUpdate = useCallback(
    async (uValue: VendorDetailsUpdateSchema) => {
      try {
        const response = await updateMutateSync(uValue);
        if (response.data) {
          return { ...uValue } as VendorDetailsQuerySchema;
        }
      } catch (error) {
        console.error(error);
        return false;
      }
      return false;
    },
    [updateMutateSync]
  );

  const handleDelete = useCallback(
    async (id: number) => {
      try {
        await deleteMutateSync({ id: id.toString() });
        return true;
      } catch (error) {
        console.error(error);
        return false;
      }
    },
    [deleteMutateSync]
  );

  const handleUpdateMultiple = useCallback(
    async (vendorDetails: VendorDetailsFormSchema[], dirtyIndices: number[] = []) => {
      if (dirtyIndices.length === 0) return true;

      const dirtyVendorDetails = dirtyIndices.map((i) => vendorDetails[i]);

      try {
        const updatePromises = dirtyVendorDetails.map((vd) =>
          handleUpdate(
            vendorDetailsFormToUpdateSingle({
              ...vd,
              inventory_id: parseInt(inventory_id),
            })
          )
        );

        const updateResults = await Promise.all(updatePromises);

        if (updateResults.some((r) => r === false)) {
          snackbar.error('Error while updating vendor details.');
          return false;
        }

        return true;
      } catch (error) {
        console.error(error);
        snackbar.error('An unexpected error occurred while updating vendor details.');
        return false;
      }
    },
    [handleUpdate, inventory_id]
  );

  const handleDeleteMultiple: CreateUpdateFn = useCallback(
    async (vendorDetails, vendors) => {
      try {
        // 1. Identify vendors that need new details created
        const vendorIds = vendors?.map((v) => v.id) ?? [];

        // 3. Identify vendors that need to be deleted
        const vendorDetailsToDelete = vendorDetails.filter((vd) => !vendorIds.includes(vd.vendor_id));

        // 3. Prepare delete operations for vendors no longer in the list
        const deletePromises = vendorDetailsToDelete.map((vd) => handleDelete(vd.id));

        // 5. Execute create and delete operations
        const deleteResults = await Promise.all(deletePromises);
        const isRefreshRequired = deleteResults.length > 0;

        if (deleteResults.some((r) => r === false)) {
          snackbar.error('Error while deleting vendor details.');
          return { result: false, isRefreshRequired };
        }

        // Filter out deleted vendor details
        const remainingVendorDetails = vendorDetails.filter((vd) => vendorIds.includes(vd.vendor_id));

        return {
          result: remainingVendorDetails,
          isRefreshRequired,
        };
      } catch (error) {
        console.error(error);
        snackbar.error('An unexpected error occurred while processing vendor details.');
        return {
          result: false,
          isRefreshRequired: true,
        };
      }
    },
    [handleDelete]
  );

  const handleCreateMultiple: CreateUpdateFn = useCallback(
    async (vendorDetails) => {
      try {
        // 1. Identify vendors that need new details created
        const missingVendorDetails = vendorDetails?.filter((v) => v.id < 0) ?? [];

        // 3. Prepare create operations for missing vendors
        const createPromises = missingVendorDetails.map((v) =>
          handleCreate({
            ...v,
            inventory_id: parseInt(inventory_id),
            vendor_id: v.vendor_id,
            last_price: currencyInput.formToUpdateConverter(v.last_price),
          })
        );

        // 4. Execute create and delete operations
        const createResults = await Promise.all(createPromises);

        const isRefreshRequired = createResults.length > 0;

        // 5. Check for errors
        if (createResults.some((r) => r === false)) {
          snackbar.error('Error while creating vendor details.');
          return { result: false, isRefreshRequired };
        }

        // 6. Combine existing and newly created vendor details, excluding deleted ones
        const onlyCreatedVDs = createResults.filter((result): result is VendorDetailsQuerySchema => result !== false);
        const missingVDWithIds = missingVendorDetails.map((v, i) => ({
          ...v,
          ...onlyCreatedVDs[i],
          uniqueIdentifier: v.id,
        }));

        const allVds = vendorDetails.map((vd) => {
          const found = missingVDWithIds.find((mvd) => mvd.uniqueIdentifier === vd.id);
          return found ?? vd;
        });

        return {
          result: allVds as VendorDetailsFormSchema[],
          isRefreshRequired,
        };
      } catch (error) {
        console.error(error);
        snackbar.error('An unexpected error occurred while processing vendor details.');
        return {
          result: false,
          isRefreshRequired: true,
        };
      }
    },
    [handleCreate, inventory_id]
  );

  const handleVendorDetails: HandleVendors = async (vendorDetails, vendors, dirtyIndices) => {
    const createRes = await handleCreateMultiple(vendorDetails, vendors);

    if (typeof createRes.result === 'boolean') {
      return createRes;
    }

    const deleteRes = await handleDeleteMultiple(createRes.result, vendors);

    if (typeof deleteRes.result === 'boolean') {
      return deleteRes;
    }

    const res = await handleUpdateMultiple(deleteRes.result, dirtyIndices);

    return {
      result: deleteRes.result,
      isRefreshRequired: res || deleteRes.isRefreshRequired || createRes.isRefreshRequired,
    };
  };

  return {
    handleVendorDetails,
    handleCreateMultiple,
    handleUpdateMultiple,
    handleDeleteMultiple,
    isVendorDetailsPending: isPending || updateIsPending || deleteIsPending,
  };
}
